import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { cn } from '../lib/cn'
import { initials } from '../lib/format'

/* ── Button ───────────────────────────────────────────────────────────────
   40px, not 56. A cursor is precise; the phone app's 56px targets are for a
   thumb. Taken from the deck's `.wbtn`.
   ────────────────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger'

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant
  icon?: ReactNode
  size?: 'md' | 'sm'
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-white shadow-[0_3px_14px_var(--color-accent-shadow)] gloss',
  outline: 'gl text-dark',
  ghost: 'bg-transparent text-sub hover:text-dark',
  danger: 'bg-transparent text-danger border border-danger/30',
}

export function Button({
  variant = 'outline',
  icon,
  size = 'md',
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center gap-2 overflow-hidden rounded-[11px]',
        'font-bold transition-transform active:scale-[0.98]',
        'disabled:pointer-events-none disabled:opacity-45',
        size === 'md' ? 'h-10 px-4 text-[13.5px]' : 'h-8 px-3 text-xs',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {icon ? <span className="z flex size-4 items-center justify-center">{icon}</span> : null}
      {children ? <span className="z">{children}</span> : null}
    </button>
  )
}

/* ── Card ─────────────────────────────────────────────────────────────────── */

export interface CardProps extends ComponentPropsWithoutRef<'div'> {
  /** `heavy` is the deck's `.gl-h` — content panels and sheets. */
  surface?: 'glass' | 'heavy' | 'accent'
  padded?: boolean
}

export function Card({
  surface = 'glass',
  padded = true,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      {...rest}
      className={cn(
        surface === 'heavy' ? 'gl-h' : surface === 'accent' ? 'gl-a' : 'gl',
        padded && 'p-[18px]',
        className,
      )}
    >
      <div className="z h-full">{children}</div>
    </div>
  )
}

/* ── Section — a titled panel with an optional trailing link ──────────────── */

export interface SectionProps {
  title?: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
  surface?: CardProps['surface']
  padded?: boolean
  className?: string
  bodyClassName?: string
  children: ReactNode
}

export function Section({
  title,
  subtitle,
  action,
  surface = 'heavy',
  padded = true,
  className,
  bodyClassName,
  children,
}: SectionProps) {
  return (
    <Card surface={surface} padded={padded} className={className}>
      {title || action ? (
        <div className="mb-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-[15px] font-bold text-dark">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs text-muted">{subtitle}</p> : null}
          </div>
          {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
        </div>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </Card>
  )
}

/* ── PageHeader ───────────────────────────────────────────────────────────── */

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="mb-5 flex items-start justify-between gap-5">
      <div className="min-w-0">
        <h1 className="font-display text-[25px] font-extrabold tracking-[-0.6px] text-dark">
          {title}
        </h1>
        {subtitle ? <p className="mt-1 text-[13px] text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  )
}

/* ── StatTile ─────────────────────────────────────────────────────────────── */

export interface StatTileProps {
  label: ReactNode
  /** Pass a `<Money>` here when the figure is money. Never a formatted string. */
  value: ReactNode
  sub?: ReactNode
  icon?: ReactNode
  className?: string
  surface?: CardProps['surface']
}

export function StatTile({ label, value, sub, icon, className, surface = 'glass' }: StatTileProps) {
  return (
    <Card surface={surface} padded={false} className={cn('px-[17px] py-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-[10.5px] font-bold tracking-[0.08em] text-muted uppercase">
            {label}
          </div>
          <div className="mt-1.5 font-display text-[27px] leading-tight font-extrabold tracking-[-0.8px] tnum text-dark">
            {value}
          </div>
          {sub ? <div className="mt-1 text-[11.5px] text-muted">{sub}</div> : null}
        </div>
        {icon ? <div className="shrink-0 text-accent-dark">{icon}</div> : null}
      </div>
    </Card>
  )
}

/* ── StatusBadge ──────────────────────────────────────────────────────────
   A status is a WORD and a colour. Never a colour alone — a dot the reader has
   to decode is not a status, and it is invisible to a colour-blind reader.
   ────────────────────────────────────────────────────────────────────────── */

export type StatusTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'accent'

const BADGE_TONES: Record<StatusTone, string> = {
  ok: 'text-success bg-success/12',
  warn: 'text-warn bg-warn/12',
  danger: 'text-danger bg-danger/12',
  neutral: 'text-sub bg-muted/15',
  accent: 'text-accent-dark bg-accent-glass',
}

export function StatusBadge({
  tone = 'neutral',
  icon,
  children,
  className,
}: {
  tone?: StatusTone
  icon?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-[3px]',
        'font-display text-[10.5px] font-bold tracking-[0.03em] whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {icon ? <span className="flex size-3 items-center justify-center">{icon}</span> : null}
      {children}
    </span>
  )
}

/* ── Meter ────────────────────────────────────────────────────────────────── */

export function Meter({
  value,
  max,
  tone,
  className,
  label,
}: {
  value: number
  /** `null` is unlimited (§4.17) — there is no bar to fill, so none is drawn. */
  max: number | null
  /** Omit to let the fill pick its own colour as the bar approaches full. */
  tone?: 'accent' | 'warn' | 'danger'
  className?: string
  label?: string
}) {
  if (max === null) return null

  const ratio = max <= 0 ? 0 : Math.min(1, Math.max(0, value / max))
  const auto = ratio >= 0.9 ? 'danger' : ratio >= 0.75 ? 'warn' : 'accent'
  const resolved = tone ?? auto
  const fill =
    resolved === 'danger' ? 'bg-danger' : resolved === 'warn' ? 'bg-warn' : 'bg-accent'

  return (
    <div
      className={cn('mt-[7px] h-2 overflow-hidden rounded-[4px] bg-muted/20', className)}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
    >
      <div className={cn('h-full rounded-[4px]', fill)} style={{ width: `${ratio * 100}%` }} />
    </div>
  )
}

/* ── Avatar ───────────────────────────────────────────────────────────────── */

/**
 * Avatar tints are drawn from the accent and neutral ramps, never from the
 * semantic palette — green means profit and red means loss in this product,
 * and a person's initials must not accidentally say either.
 */
const AVATAR_TINTS = [
  'bg-accent',
  'bg-accent-dark',
  'bg-accent-glow',
  'bg-sub',
  'bg-muted',
  'bg-dark',
] as const

function tintFor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 997
  return AVATAR_TINTS[hash % AVATAR_TINTS.length] ?? AVATAR_TINTS[0]
}

export function Avatar({
  name,
  size = 'md',
  className,
}: {
  name: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const dimension =
    size === 'lg'
      ? 'size-[34px] text-xs'
      : size === 'md'
        ? 'size-[30px] text-[11px]'
        : 'size-6 text-[10px]'

  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-[10px]',
        'font-display font-extrabold tracking-[-0.3px] text-white',
        dimension,
        tintFor(name),
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}

/* ── KeyValue — the deck's `.kv`, used all over both consoles ─────────────── */

export function KeyValue({
  label,
  value,
  className,
}: {
  label: ReactNode
  value: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 border-b border-muted/15 py-2.5 last:border-b-0',
        className,
      )}
    >
      <span className="text-[13px] text-sub">{label}</span>
      <span className="font-display text-[13px] font-bold tnum text-dark">{value}</span>
    </div>
  )
}

/* ── Banner — the deck's `.wbanner`. Always carries its own action inline. ── */

export function Banner({
  tone = 'warn',
  icon,
  title,
  body,
  action,
  className,
}: {
  tone?: 'warn' | 'danger' | 'accent'
  icon?: ReactNode
  title: ReactNode
  body?: ReactNode
  action?: ReactNode
  className?: string
}) {
  const tones = {
    warn: 'bg-warn/10 border-warn/30 text-warn',
    danger: 'bg-danger/10 border-danger/30 text-danger',
    accent: 'bg-accent-glass border-accent-glass-border text-accent-dark',
  } as const

  return (
    <div
      className={cn(
        'mb-[18px] flex items-center gap-3.5 rounded-r2 border px-[17px] py-[15px]',
        tones[tone],
        className,
      )}
      role="status"
    >
      {icon ? <span className="flex size-5 shrink-0 items-center justify-center">{icon}</span> : null}
      <div className="flex-1">
        <b className="block font-display text-sm font-bold text-dark">{title}</b>
        {body ? <span className="text-[12.5px] text-sub">{body}</span> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}
